// hooks/useMute.ts — reactive hook for mute state of a single handle.
//
// Mirrors useFollow.ts exactly. Reads from muteStore reactively. Exposes toggleMute.
// Components must NOT import muteStore or usersService directly.

import { useCallback } from 'react';
import { useMuteStore } from '@/core/stores/muteStore';
import { normHandle } from '@/core/stores/followStore';
import { usersService } from '@/core/services/usersService';

export interface MuteHook {
  /** True when the current user has muted this handle. */
  isMuted: boolean;
  /** Toggle mute state. Returns the new state, or null on failure. */
  toggleMute: () => Promise<boolean | null>;
}

export function useMute(handle: string): MuteHook {
  const isMuted = useMuteStore((s) => s.muted[normHandle(handle)]) === true;

  return {
    isMuted,
    toggleMute: () => usersService.toggleMute(handle),
  };
}

/**
 * A "have I muted this handle?" check for lists the post store doesn't hold (the Videos
 * page's feeds), so a fresh mute clears that creator at once, as it does in the feed.
 * Re-renders the caller when any mute changes.
 */
export function useIsMuted(): (handle: string) => boolean {
  const muted = useMuteStore((s) => s.muted);
  return useCallback((handle: string) => muted[normHandle(handle)] === true, [muted]);
}
